import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Sparkles, CheckCircle2, AlertCircle, Eye, EyeOff } from "lucide-react";
import RudrasLogo from "../components/RudrasLogo";
import { toast } from "sonner";
import { normalizeWebsiteUrl } from "../lib/url";
import api from "../lib/api";
import { formatApiError } from "../lib/errors";
import BrandCinematic from "../components/BrandCinematic";
import PublicFooter from "../components/PublicFooter";
import { BUSINESS_IDENTITY, SMS_CONSENT_TEXT, SMS_CONSENT_TEXT_VERSION, buildSmsConsentPayload } from "../lib/business_identity";

export function LoginPage() {
  const { login, updateToken } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  // Stabilization phase (Feb 2026): honor a `?next=/some/path` query param so
  // a session-expired bounce returns the user to where they were.
  const nextPath = (() => {
    const raw = searchParams.get("next") || "";
    // Only allow same-origin pathnames — never an absolute URL.
    if (raw && raw.startsWith("/") && !raw.startsWith("//")) return raw;
    return "/dashboard";
  })();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  // Phase 2 — if backend rejects login because the user is unverified,
  // we surface an inline verify step so they can finish the OTP flow.
  const [verify, setVerify] = useState({ active: false, email: "", devCode: "", code: "" });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(form.email, form.password, "customer");
      toast.success(`Welcome back, ${u.full_name}`);
      nav(nextPath);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err.response?.data?.detail;
      // Backend-unreachable disambiguation — preview env may auto-sleep.
      if (status === 404 || status === 502 || status === 503 || status === 504 || err.code === "ERR_NETWORK") {
        toast.error(
          "Backend is unreachable. If you're on the preview environment, click 'Wake up servers' in the bottom banner. Otherwise contact Emergent Support.",
          { duration: 9000 },
        );
      } else if (detail && typeof detail === "object" && detail.code === "email_not_verified") {
        try {
          const { data } = await api.post("/auth/send-verification", { email: detail.email });
          setVerify({ active: true, email: detail.email, devCode: data?.dev_code || "", code: "" });
          toast.message("Please verify your email to continue. We sent you a fresh 6-digit code.");
        } catch {
          setVerify({ active: true, email: detail.email, devCode: "", code: "" });
          toast.message(detail.message || "Please verify your email to continue.");
        }
      } else {
        toast.error(formatApiError(err, "Login failed"));
      }
    } finally { setLoading(false); }
  };

  const submitVerify = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(verify.code)) {
      toast.error("Enter the 6-digit code.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-email", { email: verify.email, code: verify.code });
      updateToken(data.token, data.user);
      toast.success(`Welcome, ${data.user.full_name}!`);
      nav(nextPath);
    } catch (err) {
      toast.error(formatApiError(err, "Could not verify. The code may have expired."));
    } finally { setLoading(false); }
  };

  const resendCode = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/send-verification", { email: verify.email });
      setVerify((s) => ({ ...s, devCode: data?.dev_code || s.devCode }));
      toast.success("A new code has been sent.");
    } catch (err) {
      toast.error(formatApiError(err, "Could not resend code."));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 md:grid md:grid-cols-2">
        <BrandCinematic variant="login" data-testid="login-brand-cinematic" />
      <div className="flex items-center justify-center p-8">
        {verify.active ? (
          <div className="w-full max-w-sm space-y-5" data-testid="login-verify-step">
            <div>
              <h1 className="font-display text-3xl font-semibold">Verify your email</h1>
              <p className="text-muted-foreground text-sm mt-1">
                Enter the 6-digit code we sent to <span className="font-medium text-foreground">{verify.email}</span>.
              </p>
            </div>
            {verify.devCode && (
              <div className="p-3.5 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-2.5" data-testid="login-dev-banner">
                <AlertCircle className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
                <div className="text-[13px] text-amber-900 leading-snug">
                  <span className="font-semibold">Development mode.</span> Your verification code is:{" "}
                  <span className="font-mono text-base font-bold tracking-[0.35em] text-emerald-800 ml-1" data-testid="login-dev-code">{verify.devCode}</span>
                </div>
              </div>
            )}
            <form onSubmit={submitVerify} className="space-y-4">
              <div>
                <Label>6-digit code</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  autoComplete="one-time-code"
                  placeholder="123456"
                  value={verify.code}
                  onChange={(e) => setVerify((s) => ({ ...s, code: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
                  className="mt-1.5 font-mono text-center text-lg tracking-[0.4em]"
                  data-testid="login-verify-code-input"
                  autoFocus
                />
              </div>
              <Button type="submit" disabled={loading || verify.code.length !== 6} className="w-full bg-primary hover:bg-primary/90" data-testid="login-verify-submit">
                {loading ? "Verifying…" : "Verify and continue"}
              </Button>
              <div className="text-center text-xs text-muted-foreground">
                Didn't get the code?{" "}
                <button type="button" onClick={resendCode} disabled={loading} className="text-accent hover:underline font-medium" data-testid="login-verify-resend">
                  Resend
                </button>
              </div>
              <div className="text-center text-xs text-muted-foreground border-t pt-3">
                <button type="button" onClick={() => setVerify({ active: false, email: "", devCode: "", code: "" })} className="underline hover:text-foreground" data-testid="login-verify-back">
                  ← Back to sign in
                </button>
              </div>
            </form>
          </div>
        ) : (
          <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
            <div>
              <h1 className="font-display text-3xl font-semibold">Log in</h1>
              <p className="text-muted-foreground text-sm mt-1">No account? <Link to="/register" className="text-accent hover:underline">Create one</Link></p>
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" required data-testid="login-email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="mt-1.5"/>
            </div>
            <div>
              <Label>Password</Label>
              <Input type="password" required data-testid="login-password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} className="mt-1.5"/>
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full bg-primary hover:bg-primary/90">{loading ? "Signing in..." : "Log in"}</Button>
            <div className="text-xs text-muted-foreground text-center">
              <Link to="/forgot-password" className="hover:text-accent hover:underline" data-testid="link-forgot-password">Forgot password?</Link>
            </div>
            <div className="text-xs text-muted-foreground border-t pt-4 text-center">
              Rudras-AI team? <Link to="/internal-admin/login" className="text-accent hover:underline" data-testid="link-internal-login">Internal admin login</Link>
            </div>
          </form>
        )}
      </div>
      </div>
      <PublicFooter />
    </div>
  );
}

export function RegisterPage() {
  const { updateToken } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("form"); // "form" | "verify"
  const [registerError, setRegisterError] = useState("");  // inline, never set on success
  const [form, setForm] = useState({
    full_name: "",
    company_name: "",
    email: "",
    password: "",
    website_url: "",
    industry: "other",
    sales_model: "b2c",
    phone: "",
  });
  const [showPwd, setShowPwd] = useState(false);
  const [confirmPwd, setConfirmPwd] = useState("");
  const [loading, setLoading] = useState(false);
  // Iter55 — A2P 10DLC SMS consent (unchecked by default per CTIA guidelines)
  const [smsConsent, setSmsConsent] = useState(false);
  // Phase 2 — OTP email verification (dev mode)
  const [verifyState, setVerifyState] = useState({
    email: "",
    devCode: "",
    code: "",
    resendAt: 0,
    isPending: false,  // Iter51 — true when this code is for a "continuing" pending registration
  });

  const pwLenOk = form.password.length >= 8;
  const pwUpperOk = /[A-Z]/.test(form.password);
  const pwOk = pwLenOk && pwUpperOk;
  // v1.0.16 — confirm-password client validation. Show the mismatch hint only
  // after the user has typed something into the confirm field so we don't
  // flash a red error on initial render.
  const pwMatch = form.password === confirmPwd;
  const pwMatchShown = confirmPwd.length > 0;
  const canSubmit = pwOk && pwMatch && confirmPwd.length > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!pwOk) {
      toast.error("Password must be at least 8 characters and contain one uppercase letter.");
      return;
    }
    if (!pwMatch) {
      toast.error("Passwords do not match.");
      return;
    }
    setLoading(true);
    // v1.0.17 — Always clear any prior verify state + error BEFORE we issue the
    // new request. This is defense-in-depth against a regression in the
    // axios interceptor or a stale render that could otherwise leave the
    // page on the "verify" step from a previous successful attempt.
    setRegisterError("");
    setVerifyState({ email: "", devCode: "", code: "", resendAt: 0, isPending: false });
    setStep("form");
    try {
      // Normalize bare/`www.` website inputs so users don't have to type the scheme.
      // Iter55 — attach A2P 10DLC SMS consent metadata when the user opted in.
      // Backend stores as informational/transactional consent only (no marketing).
      // Iter55 → Twilio A2P 10DLC re-submission (Feb 2026) — attach
      // the full consent envelope (version-pinned text, source URL,
      // timestamp) so a future audit can prove exactly what the user
      // agreed to. Backend hashes the request IP and stores
      // `sms_consent_ip_hash`.
      const consentPayload = buildSmsConsentPayload({
        granted: smsConsent === true,
        source: "registration_form",
      });
      const payload = {
        ...form,
        website_url: normalizeWebsiteUrl(form.website_url),
        ...consentPayload,
      };
      // Hit the API directly so we get the raw response (which now contains
      // requires_verification + dev_code instead of a JWT).
      const resp = await api.post("/auth/register", payload);
      const data = resp?.data;
      // v1.0.17 — STRICT navigation guard. We advance to the verify step
      // ONLY when ALL of:
      //   1. HTTP status is 2xx (axios resolves only on 2xx, but be explicit).
      //   2. `data.requires_verification` is the literal boolean `true`.
      //   3. `data.email` echoes back (otherwise we can't even resend).
      // Any other shape means the backend either failed soft or returned a
      // legacy response — we surface that as an error and stay on the form.
      const ok2xx = resp?.status >= 200 && resp?.status < 300;
      const advanced = ok2xx && data?.requires_verification === true && !!data?.email;
      if (advanced) {
        setVerifyState({
          email: data.email,
          devCode: data.dev_code || "",
          code: "",
          resendAt: Date.now(),
          isPending: !!data.pending_existing,
        });
        setStep("verify");
        // Iter51 — if the backend tells us we're resuming an existing
        // pending registration, surface that to the customer so they
        // understand why a "new code" message appears for a familiar
        // email. Otherwise show the regular fresh-signup toast.
        if (data.pending_existing) {
          toast.message(
            data.message ||
              "This email is pending verification. We sent a new code. Please verify to continue."
          );
        } else {
          toast.success("Account created. Enter the 6-digit code to verify your email.");
        }
      } else if (data?.token) {
        // Defensive — should never happen in Phase 2 but keeps the path safe.
        updateToken(data.token, data.user);
        nav("/dashboard?welcome=1");
      } else {
        // Unexpected shape — backend did not raise but we cannot proceed.
        // Surface a clear, actionable inline error.
        const msg = "Registration could not complete. Please try again or contact support.";
        setRegisterError(msg);
        toast.error(msg);
      }
    } catch (err) {
      // v1.0.5 — temporary production-safe diagnostic logging so we can
      // pinpoint any future regression in the validation error path. Logged
      // only inside the catch (no PII unless the backend echoes user input
      // back in its 422 response — Pydantic does echo bad email/password).
      try {
        // eslint-disable-next-line no-console
        console.log("REGISTER_ERROR_PAYLOAD", err?.response?.status, err?.response?.data);
        // eslint-disable-next-line no-console
        console.log("FORMATTED_REGISTER_ERROR", formatApiError(err, "Registration failed"));
      } catch (_) { /* never break the error path on logging */ }
      // Defensive — formatApiError() always returns a SAFE string. Never
      // pass `err.response.data.detail` directly to toast: when the backend
      // returns a 422 (array) or a structured object, sonner tries to render
      // it as JSX and React crashes with "Objects are not valid as a React
      // child", taking the whole register page down with it.
      const msg = formatApiError(err, "Registration failed");
      setRegisterError(msg);
      toast.error(msg);
      // v1.0.17 — Final guarantee: never end up on the verify step after an
      // error. This is redundant with the `setStep("form")` at the top of
      // the try, but cheap to keep as a belt-and-braces.
      setStep("form");
    } finally { setLoading(false); }
  };

  const verifyCode = async () => {
    const code = (verifyState.code || "").trim();
    if (!/^\d{6}$/.test(code)) {
      toast.error("Enter the 6-digit code.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-email", { email: verifyState.email, code });
      if (data?.token) {
        updateToken(data.token, data.user);
        toast.success(`Welcome, ${data.user.full_name}! Your ${data?.company?.plan_status || "trial"} starts now.`);
        nav("/dashboard?welcome=1");
      } else {
        toast.error("Verification succeeded but no session was issued. Please log in.");
        nav("/login");
      }
    } catch (err) {
      toast.error(formatApiError(err, "Could not verify. The code may have expired."));
    } finally { setLoading(false); }
  };

  const resendCode = async () => {
    if (Date.now() - verifyState.resendAt < 15000) {
      toast.message("Please wait a few seconds before requesting another code.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/send-verification", { email: verifyState.email });
      setVerifyState((s) => ({ ...s, devCode: data?.dev_code || "", resendAt: Date.now() }));
      toast.success("A new code has been sent.");
    } catch (err) {
      toast.error(formatApiError(err, "Could not resend code."));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 md:grid md:grid-cols-2">
      <div className="flex items-center justify-center p-8 md:order-2">
        {step === "form" ? (
          <form onSubmit={submit} className="w-full max-w-md space-y-4" data-testid="register-form">
            <div>
              <h1 className="font-display text-3xl font-semibold">Create your assistant</h1>
              <p className="text-muted-foreground text-sm mt-1">14-day free trial · No card required. Already have an account? <Link to="/login" className="text-accent hover:underline">Log in</Link></p>
            </div>
            {registerError && (
              <div
                role="alert"
                data-testid="register-inline-error"
                className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2.5 text-sm text-rose-800 flex items-start gap-2"
              >
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span className="leading-snug">{registerError}</span>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Your name</Label>
                <Input required data-testid="reg-name" value={form.full_name} onChange={(e) => setForm({...form, full_name: e.target.value})} className="mt-1.5"/>
              </div>
              <div>
                <Label>Business name</Label>
                <Input required data-testid="reg-company" value={form.company_name} onChange={(e) => setForm({...form, company_name: e.target.value})} className="mt-1.5"/>
              </div>
            </div>
            <div>
              <Label>Work email</Label>
              <Input type="email" required data-testid="reg-email" value={form.email} onChange={(e) => { setForm({...form, email: e.target.value}); if (registerError) setRegisterError(""); }} className="mt-1.5"/>
            </div>
            <div>
              <Label>Password</Label>
              <div className="relative mt-1.5">
                <Input
                  type={showPwd ? "text" : "password"}
                  required
                  data-testid="reg-password"
                  value={form.password}
                  onChange={(e) => setForm({...form, password: e.target.value})}
                  placeholder="At least 8 chars, 1 uppercase"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute inset-y-0 right-2 my-auto h-7 w-7 grid place-items-center text-slate-500 hover:text-slate-800"
                  tabIndex={-1}
                  aria-label={showPwd ? "Hide password" : "Show password"}
                  data-testid="reg-password-toggle"
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="mt-1.5 flex items-center gap-3 text-[11.5px]">
                <span className={`inline-flex items-center gap-1 ${pwLenOk ? "text-emerald-700" : "text-slate-500"}`} data-testid="reg-rule-length">
                  {pwLenOk ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                  At least 8 characters
                </span>
                <span className={`inline-flex items-center gap-1 ${pwUpperOk ? "text-emerald-700" : "text-slate-500"}`} data-testid="reg-rule-uppercase">
                  {pwUpperOk ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                  1 uppercase letter
                </span>
              </div>
            </div>
            <div>
              <Label>Confirm password</Label>
              <Input
                type={showPwd ? "text" : "password"}
                required
                data-testid="reg-confirm-password"
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                placeholder="Re-enter your password"
                className="mt-1.5"
              />
              {pwMatchShown && !pwMatch && (
                <div
                  className="mt-1.5 inline-flex items-center gap-1 text-[11.5px] text-rose-700"
                  data-testid="reg-confirm-mismatch"
                >
                  <AlertCircle className="w-3 h-3" />
                  Passwords do not match.
                </div>
              )}
              {pwMatchShown && pwMatch && (
                <div
                  className="mt-1.5 inline-flex items-center gap-1 text-[11.5px] text-emerald-700"
                  data-testid="reg-confirm-match"
                >
                  <CheckCircle2 className="w-3 h-3" />
                  Passwords match.
                </div>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Website URL</Label>
                <Input type="text" placeholder="yourcompany.com" data-testid="reg-website" value={form.website_url} onChange={(e) => setForm({...form, website_url: e.target.value})} onBlur={(e) => setForm({...form, website_url: normalizeWebsiteUrl(e.target.value)})} className="mt-1.5"/>
              </div>
              <div>
                <Label>Mobile Phone Number</Label>
                <Input data-testid="reg-phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="mt-1.5" placeholder="+1 555 123 4567" inputMode="tel"/>
                <p className="mt-1 text-[11px] text-muted-foreground" data-testid="reg-phone-help">
                  Optional unless you want SMS verification &amp; service notifications.
                </p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Industry</Label>
                <select required data-testid="reg-industry" value={form.industry} onChange={(e) => setForm({...form, industry: e.target.value})} className="mt-1.5 w-full rounded-md border bg-background px-3 py-2 text-sm">
                  <option value="other">Other</option>
                  <option value="restaurant">Restaurant / Food</option>
                  <option value="retail">Retail / E-commerce</option>
                  <option value="healthcare">Healthcare / Clinic</option>
                  <option value="saas">SaaS / Tech</option>
                  <option value="education">Education / Training</option>
                  <option value="services">Professional services</option>
                  <option value="real_estate">Real estate</option>
                  <option value="salon">Salon / Wellness</option>
                  <option value="nonprofit">Non-profit</option>
                </select>
              </div>
              <div>
                <Label>Sales model</Label>
                <select required data-testid="reg-sales-model" value={form.sales_model} onChange={(e) => setForm({...form, sales_model: e.target.value})} className="mt-1.5 w-full rounded-md border bg-background px-3 py-2 text-sm">
                  <option value="b2c">B2C — selling to consumers</option>
                  <option value="b2b">B2B — selling to businesses</option>
                  <option value="both">Both</option>
                </select>
              </div>
            </div>
            <Button type="submit" disabled={loading || !canSubmit} data-testid="reg-submit" className="w-full bg-primary hover:bg-primary/90">{loading ? "Creating..." : "Create Assistant"}</Button>
            <p className="text-xs text-muted-foreground">By creating an account you agree to our <Link to="/terms-of-service" className="underline">terms</Link> and <Link to="/privacy-policy" className="underline">privacy policy</Link>.</p>

            {/* Twilio A2P 10DLC compliance — unchecked by default per
                CTIA guidelines. Wording is version-pinned in
                SMS_CONSENT_TEXT so the exact text the user agreed to
                can be reconstructed during a Twilio audit. The
                checkbox is OPTIONAL — registration succeeds whether
                it is checked or not. */}
            <div data-testid="reg-sms-consent-block" className="rounded-lg border border-border bg-card/40 px-3 py-2.5">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground/80" data-testid="reg-sms-consent-optional-badge">
                  Optional SMS consent
                </span>
                <span className="text-[10px] text-muted-foreground/60" data-testid="reg-sms-consent-version">
                  {SMS_CONSENT_TEXT_VERSION}
                </span>
              </div>
              <label
                data-testid="reg-sms-consent-label"
                className="flex items-start gap-2.5 cursor-pointer"
              >
                <input
                  type="checkbox"
                  data-testid="reg-sms-consent"
                  checked={smsConsent}
                  onChange={(e) => setSmsConsent(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-primary cursor-pointer"
                  aria-describedby="reg-sms-consent-text"
                />
                <span id="reg-sms-consent-text" className="text-xs leading-relaxed text-muted-foreground" data-testid="reg-sms-consent-text">
                  I agree to receive SMS messages from {BUSINESS_IDENTITY.tradeName} regarding account verification,
                  service notifications, customer support updates, order confirmations, and order status updates.
                  Message frequency varies. Message and data rates may apply. Reply <strong>STOP</strong> to opt out.
                  Reply <strong>HELP</strong> for assistance. <strong>Consent is not a condition of purchase or use
                  of {BUSINESS_IDENTITY.tradeName} services.</strong> View our
                  {" "}<Link to="/privacy-policy" className="underline" target="_blank" rel="noopener">Privacy Policy</Link>,
                  {" "}<Link to="/terms-of-service" className="underline" target="_blank" rel="noopener">Terms &amp; Conditions</Link>,
                  and <Link to="/sms-program" className="underline" target="_blank" rel="noopener">SMS Program</Link>.
                </span>
              </label>
              <p className="mt-1.5 text-[10px] text-muted-foreground/70" data-testid="reg-sms-consent-optional-help">
                Leaving this unchecked will not block your account creation — you simply won&apos;t receive SMS notifications.
              </p>
            </div>
          </form>
        ) : (
          <div className="w-full max-w-md space-y-5" data-testid="register-verify-step">
            <div>
              <h1 className="font-display text-3xl font-semibold">Verify your email</h1>
              <p className="text-muted-foreground text-sm mt-1">
                We sent a 6-digit code to <span className="font-medium text-foreground">{verifyState.email}</span>. It expires in 30 minutes.
              </p>
            </div>
            {verifyState.isPending && (
              <div
                role="status"
                data-testid="register-pending-banner"
                className="rounded-lg border border-sky-300 bg-sky-50 px-3 py-2.5 text-sm text-sky-900 flex items-start gap-2"
              >
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span className="leading-snug">
                  This email is pending verification. We sent a new code. Please verify to continue.
                </span>
              </div>
            )}
            {verifyState.devCode && (
              <div className="p-3.5 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-2.5" data-testid="register-dev-banner">
                <AlertCircle className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
                <div className="text-[13px] text-amber-900 leading-snug">
                  <span className="font-semibold">Development mode.</span> Your verification code is:{" "}
                  <span className="font-mono text-base font-bold tracking-[0.35em] text-emerald-800 ml-1" data-testid="register-dev-code">{verifyState.devCode}</span>
                </div>
              </div>
            )}
            <form onSubmit={(e) => { e.preventDefault(); verifyCode(); }} className="space-y-4">
              <div>
                <Label htmlFor="reg-verify-code">6-digit code</Label>
                <Input
                  id="reg-verify-code"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  autoComplete="one-time-code"
                  placeholder="123456"
                  value={verifyState.code}
                  onChange={(e) => setVerifyState((s) => ({ ...s, code: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
                  className="mt-1.5 font-mono text-center text-lg tracking-[0.4em]"
                  data-testid="register-verify-code-input"
                  disabled={loading}
                  autoFocus
                />
              </div>
              <Button type="submit" disabled={loading || verifyState.code.length !== 6} className="w-full bg-primary hover:bg-primary/90" data-testid="register-verify-submit">
                {loading ? "Verifying…" : "Verify and continue"}
              </Button>
              <div className="text-center text-xs text-muted-foreground">
                Didn't receive the code?{" "}
                <button type="button" onClick={resendCode} disabled={loading} className="text-accent hover:underline font-medium" data-testid="register-verify-resend">
                  Resend
                </button>
              </div>
              <div className="text-center text-xs text-muted-foreground border-t pt-3">
                <button type="button" onClick={() => setStep("form")} className="underline hover:text-foreground" data-testid="register-verify-back">
                  ← Use a different email
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
      <div className="hidden md:block md:order-1">
        <BrandCinematic variant="register" className="h-full" data-testid="register-brand-cinematic" />
      </div>
      </div>
      <PublicFooter />
    </div>
  );
}


// Forgot password — real flow. Backend issues a 30-minute, single-use reset token
// and emails a link to /reset-password?token=… via SendGrid. We always show the same
// confirmation regardless of whether the email matched an account (anti-enumeration).
export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim(), portal: "customer" });
      setSubmitted(true);
    } catch (err) {
      // Backend should always return 200; surface unexpected errors but keep the
      // confirmation generic so we don't leak whether the email exists.
      toast.error(formatApiError(err, "Could not send reset link. Please try again."));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen flex flex-col" data-testid="forgot-password-page">
      <div className="flex-1 grid place-items-center p-8">
      <div className="w-full max-w-sm space-y-5">
        <Link to="/" className="flex items-center gap-2">
          <RudrasLogo size="md" />
          <span className="font-display font-semibold text-xl">Rudras-AI</span>
        </Link>
        <h1 className="font-display text-3xl font-semibold">Reset your password</h1>
        {!submitted ? (
          <form onSubmit={submit} className="space-y-4" data-testid="forgot-password-form">
            <div>
              <Label>Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" data-testid="forgot-email"/>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-primary hover:bg-primary/90" data-testid="forgot-submit">
              {loading ? "Sending…" : "Send reset link"}
            </Button>
          </form>
        ) : (
          <div className="rounded-md border bg-muted/40 p-4 text-sm" data-testid="forgot-submitted">
            <div className="font-medium mb-1">Check your inbox.</div>
            <div className="text-muted-foreground">If <code>{email}</code> matches an account, a one-time reset link is on its way. The link expires in 30 minutes.</div>
            <div className="text-xs text-muted-foreground mt-3 border-t pt-3">Didn&apos;t receive it after a few minutes? Check spam, or contact <a href="mailto:support@rudras-ai.com" className="underline">support@rudras-ai.com</a>.</div>
          </div>
        )}
        <div className="text-sm"><Link to="/login" className="text-accent hover:underline">← Back to login</Link></div>
      </div>
      </div>
      <PublicFooter />
    </div>
  );
}


// Reset password — consumes the token from the email link. Token is single-use
// and expires after 30 minutes (enforced server-side).
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Missing reset token — please use the link from your email.");
    }
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (pwd !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd, portal: "customer" });
      setDone(true);
      toast.success("Password updated. You can sign in now.");
      setTimeout(() => nav("/login", { replace: true }), 1500);
    } catch (err) {
      toast.error(formatApiError(err, "Could not reset password."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="reset-password-page">
      <div className="flex-1 grid place-items-center p-8">
      <div className="w-full max-w-sm space-y-5">
        <Link to="/" className="flex items-center gap-2">
          <RudrasLogo size="md" />
          <span className="font-display font-semibold text-xl">Rudras-AI</span>
        </Link>
        <h1 className="font-display text-3xl font-semibold">Choose a new password</h1>
        {!token ? (
          <div className="rounded-md border bg-muted/40 p-4 text-sm" data-testid="reset-no-token">
            <div className="font-medium mb-1">This link is invalid.</div>
            <div className="text-muted-foreground">Please request a new reset link from the <Link to="/forgot-password" className="text-accent hover:underline">forgot password</Link> page.</div>
          </div>
        ) : done ? (
          <div className="rounded-md border bg-muted/40 p-4 text-sm" data-testid="reset-success">
            <div className="font-medium mb-1">Password updated.</div>
            <div className="text-muted-foreground">Redirecting you to login…</div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
            <div>
              <Label>New password</Label>
              <Input type="password" required minLength={8} value={pwd} onChange={(e) => setPwd(e.target.value)} className="mt-1.5" data-testid="reset-password-input"/>
              <div className="text-xs text-muted-foreground mt-1">At least 8 characters.</div>
            </div>
            <div>
              <Label>Confirm new password</Label>
              <Input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5" data-testid="reset-password-confirm"/>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-primary hover:bg-primary/90" data-testid="reset-submit">
              {loading ? "Updating…" : "Update password"}
            </Button>
          </form>
        )}
        <div className="text-sm"><Link to="/login" className="text-accent hover:underline">← Back to login</Link></div>
      </div>
      </div>
      <PublicFooter />
    </div>
  );
}
